import { useState, useRef, useEffect } from 'react';
import { Form, Upload, Button, message, Select, Card, Row, Col, Table, Typography, List, Tabs } from 'antd';
import type { UploadProps } from 'antd';
import { FileExcelOutlined, UploadOutlined } from '@ant-design/icons';
import { post } from '@/common/services/httpClient';
import { API_ENDPOINTS, QUERY_KEYS } from '../../api/apiEndpoints';
import { useAppMutation } from '@/common/hooks/useAppMutation';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import type { UploadFile, RcFile } from 'antd/es/upload/interface';
import type { ColumnsType } from 'antd/es/table';
import Input from 'antd/es/input/Input';
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

type CellValue = string | number | boolean | null;

interface InvalidRecord {
    [key: string]: CellValue | { [key: string]: string };
    nationalIDNo: string;
    firstName: string;
    lastName: string;
    relation: string;
    gender: string;
    validationErrors: { [key: string]: string };
}

interface UploadSummary {
    totalRecords: number;
    savedRecords: number;
    failedRecords: number;
}

interface BulkUploadResponse {
    isError: boolean;
    message: string;
    totalRecords: number;
    savedRecords: number;
    failedRecords: number;
    invalidRecords?: InvalidRecord[];
}

// Function to format snake_case or camelCase to Title Case
const toTitleCase = (str: string) => {
    const result = str.replace(/([A-Z])/g, " $1");
    return result.charAt(0).toUpperCase() + result.slice(1);
};

export default function PolicyHolderBulkForm() {
    const [form] = Form.useForm();
    const [file, setFile] = useState<File | null>(null);
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [invalidRecords, setInvalidRecords] = useState<InvalidRecord[]>([]);
    const [uploadSummary, setUploadSummary] = useState<UploadSummary | null>(null);
    const [uploadType, setUploadType] = useState<'individual' | 'company'>('company');
    const navigate = useNavigate();
    const resultsRef = useRef<HTMLDivElement>(null);
    const [selectedCustomer, setSelectedCustomer] = useState<number | null>(null);

    const { t } = useTranslation();

    useEffect(() => {
        if (uploadSummary && invalidRecords.length > 0) {
            resultsRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [uploadSummary, invalidRecords]);

    const { data: companyOptions = [], isLoading: loadingCompanies } = useQuery({
        queryKey: [QUERY_KEYS.PARTICIPANTS.COMPANY_BULK, selectedCustomer],
        enabled: !!selectedCustomer, // only run after customer selected
        queryFn: async () => {
            try {
                const response = await post<{ data: { name: string; id: number }[] }>(API_ENDPOINTS.PARTICIPANTS.SEARCH_COMPANY,
                    { StatusId: 1, CustomerId: selectedCustomer  }
                );
                return response.data.map(item => ({ label: item.name, value: item.id }));
            } catch (error) {
                message.error(t('Failed to load companies'));
                return [];
            }
        },
        // refetchOnWindowFocus: true,   // refresh on tab/window focus
        refetchOnMount: "always",     // force refresh every time page/component opens
        // staleTime: 0,    
    });

    const { data: customerOptions = [], isLoading: loadingCustomers } = useQuery({
        queryKey: ['customers'],
        queryFn: async () => {
            try {
                const response = await post<{ data: { name: string; id: number }[] }>(API_ENDPOINTS.PARTICIPANTS.SEARCH_CUSTOMER, { Status: 'Active' });
                return response.data.map(item => ({ label: item.name, value: item.id }));
            } catch (error) {
                message.error(t('Failed to load companies.'));
                return [];
            }
        },
        refetchOnWindowFocus: true,   // refresh on tab/window focus
    });

    const uploadMutation = useAppMutation(
        (formData: FormData) => {
            return post<BulkUploadResponse>(API_ENDPOINTS.PARTICIPANTS.SAVE_BULK_POLICYHOLDER, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        },
        {
            errorMessagePrefix: t('Upload failed'),
            invalidateQueryKey: QUERY_KEYS.PARTICIPANTS.POLICYHOLDERS,
            onSuccess: (response) => {
                setUploadSummary({
                    totalRecords: response.totalRecords,
                    savedRecords: response.savedRecords,
                    failedRecords: response.failedRecords,
                });

                if (response.invalidRecords && response.invalidRecords.length > 0) {
                    message.warning(t(response.message) || t('Some records have validation errors.'));
                    setInvalidRecords(response.invalidRecords);
                } else {

                    if (response.isError) {
                        message.error(t(response.message) || t('Error.'));
                    } else {
                        message.success(t(response.message) || t('Policy holders uploaded successfully.'));
                    }
                    setInvalidRecords([]);
                    form.resetFields();
                    setFile(null);
                    setFileList([]);
                }
            },
        }
    );

    const onFinish = async (values: { companyId: number; companyName: string; customerId: number; customerName: string }) => {
        if (!file) {
            message.error(t('Please upload an Excel file.'));
            return;
        }
        setInvalidRecords([]);
        setUploadSummary(null);
        const formData = new FormData();
        formData.append('File', file, file.name);
        // Always append CompanyId � 0 for individual, selected value for company
        const companyId = uploadType === 'company' ? values.companyId ?? 0 : 0;
        const companyName = uploadType === 'company' ? values.companyName ?? '' : '';
        const customerId = values.customerId ?? 0;
        const customerName = values.customerName ?? '';

        formData.append('CompanyId', String(companyId));
        formData.append('CompanyName', companyName);
        formData.append('CustomerId', String(customerId));
        formData.append('CustomerName', customerName);

        uploadMutation.mutate(formData);
    };

    const uploadProps: UploadProps = {
        accept: ".xlsx,.xls",
        fileList,
        beforeUpload: (uploadedFile: RcFile) => {
            setFile(uploadedFile);
            setFileList([uploadedFile]);
            setInvalidRecords([]);
            setUploadSummary(null);
            return false;
        },
        onRemove: () => {
            setFile(null);
            setFileList([]);
            setInvalidRecords([]);
            setUploadSummary(null);
            return true;
        },
        maxCount: 1,
    };

    const generateColumns = (records: InvalidRecord[]): ColumnsType<InvalidRecord> => {
        if (records.length === 0) return [];

        const firstRecord = records[0];
        const keys = Object.keys(firstRecord);

        const excludedKeys = new Set(['validationErrors', 'errorInBulk', 'isActive', 'companyId', 'cityId', 'countryId']);
        const primaryKey = 'nationalIDNo';

        const dynamicKeys = keys.filter(key => key !== primaryKey && !excludedKeys.has(key));

        const renderCell = (text: CellValue, record: InvalidRecord, key: string) => {
            
            const errorKey = Object.keys(record.validationErrors).find(errKey => errKey.toLowerCase() === key.toLowerCase());
            const hasError = !!errorKey;
            const cellStyle = hasError ? { backgroundColor: '#fff1f0', color: '#cf1322', padding: '16px', margin: '-16px' } : {};
            return <div style={cellStyle}>{String(text)}</div>;
        };

        const primaryColumn: ColumnsType<InvalidRecord>[0] = {
            title: toTitleCase(primaryKey),
            dataIndex: primaryKey,
            key: primaryKey,
            fixed: 'left',
            width: 150,
            /*render: (text: CellValue, record: InvalidRecord) => renderCell(text, record, primaryKey),*/
            render: (text: CellValue, record: InvalidRecord) =>
                text === null || text === undefined || text === "" ? "" : renderCell(text, record, primaryKey),
        };

        const dynamicColumns: ColumnsType<InvalidRecord> = dynamicKeys.map(key => ({
            title: key === "bloodGroupString" ? "Blood Group" : toTitleCase(key),
            dataIndex: key,
            key: key,
            render: (text: CellValue, record: InvalidRecord) =>
                text === null || text === undefined || text === "" ? "" : renderCell(text, record, key),
        }));

        return [primaryColumn, ...dynamicColumns];
    };

    const tableColumns = generateColumns(invalidRecords);

    const columnTitleMap: Record<string, string> = {
        //title: "Title", firstName: "First Name", mname: "Mname", lastName: "Last Name", nationalIDNo: "National ID No", parentNationalIDNo: "Parent National ID No", mobileNo: "Mobile No", dob: "DOB", gender: "Gender", maritalStatus: "Marital Status", relation: "Relation", country: "Country", city: "City", area: "Area", streetNo: "Street No", address: "Address", bloodGroup: "Blood Group", category: "Category",
        title: "Title", firstName: "First Name", mname: "Mname", lastName: "Last Name", nationalIDNo: "National ID No", mobileNo: "Mobile No", dob: "DOB", gender: "Gender", maritalStatus: "Marital Status", country: "Country", city: "City", area: "Area", streetNo: "Street No", address: "Address", bloodGroupString: "Blood Group", category: "Category",
    };

    const exportInvalidRecordsWithStyles = async (records: InvalidRecord[], columnsToExport: string[]) => {
        if (!records || records.length === 0) {
            message.warning(t("No invalid records to export."));
            return;
        }

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet(t("Invalid Records"));

        // Build headers
        const headers = columnsToExport.map((col) => ({
            header: columnTitleMap[col] || toTitleCase(col),
            key: col,
            width: 20,
        }));

        worksheet.columns = headers;

        // Add rows with style
        records.forEach((record) => {
            const rowData: Record<string, any> = {};

            columnsToExport.forEach((col) => {
                rowData[col] = record[col as keyof InvalidRecord] ?? "";
            });

            const row = worksheet.addRow(rowData);

            // Apply red styling if that field has a validation error
            columnsToExport.forEach((col, idx) => {
                const cell = row.getCell(idx + 1);
                const hasError =
                    record.validationErrors &&
                    Object.keys(record.validationErrors).some(
                        (errKey) => errKey.toLowerCase() === col.toLowerCase()
                    );

                if (hasError) {
                    cell.font = { color: { argb: "FFFF0000" } }; // red text
                    cell.fill = {
                        type: "pattern",
                        pattern: "solid",
                        fgColor: { argb: "FFFFE5E5" }, // light red background
                    };
                }
            });
        });

        // Export
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), "Invalid_Policy_Holders.xlsx");
    };

    const expandedRowRender = (record: InvalidRecord) => {
        const errors = Object.entries(record.validationErrors);
        return (
            <List
                header={<Text strong>Validation Errors</Text>}
                dataSource={errors}
                renderItem={([field, error]) => (
                    <List.Item>
                        <Text strong>{toTitleCase(field)}:</Text> <Text type="danger">{error}</Text>
                    </List.Item>
                )}
                bordered
                size="small"
            />
        );
    };

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                <Button htmlType="button" onClick={() => navigate({ to: '..' })}>
                    {t("Go Back")}
                </Button>
            </div>
            <Form form={form} layout="vertical" onFinish={onFinish}>
                <Card
                    title={

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>{t("Bulk Upload Policy Holders")}</span>
                            <a href="/bulk-policyholder-template.xlsx" download>
                                <Button icon={<UploadOutlined />} style={{ backgroundColor: '#e6f7ff', borderColor: '#91d5ff', color: '#096dd9' }} > {t("Download Template")} </Button>
                            </a>
                        </div>
                    }
                >
                    <Tabs
                        defaultActiveKey="company"
                        onChange={(key) => setUploadType(key as 'individual' | 'company')}
                        style={{ marginBottom: 16 }}
                        items={[
                            {
                                key: 'company',
                                label: t('Company'),
                            },
                            //{
                            //    key: 'individual',
                            //    label: t('Individual'),
                            //},
                        ]}
                    />
                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item
                                    name="customerId"
                                label={t("Customer")}
                                    rules={[{ required: true, message: t('Please select a customer') }]}
                                >
                                    <Select
                                        size="large"
                                        placeholder={t("Select a customer")}
                                        loading={loadingCustomers}
                                        options={customerOptions}
                                        onChange={(value, label) => {
                                            // set selected customer to trigger company fetch
                                            setSelectedCustomer(value);

                                            const customerLabel =
                                                typeof label === 'string'
                                                    ? label
                                                    : customerOptions.find((opt) => opt.value === value)?.label || '';

                                            //console.log('customerLabel label ', customerLabel);
                                            form.setFieldsValue({ customerName: customerLabel });

                                            // reset company fields when new customer selected
                                            form.resetFields(['companyId', 'companyName']);
                                        }}
                                        showSearch
                                        filterOption={(input, option) =>
                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                        }
                                    />
                                </Form.Item>
                                <Form.Item name="customerName" noStyle>
                                    <Input type="hidden" />
                                </Form.Item>
                            </Col>

                        {/* Company Dropdown */}
                        {uploadType === 'company' && (
                            <Col span={8}>
                                <Form.Item
                                    name="companyId"
                                    label={t("Company")}
                                    rules={[{ required: true, message: t('Please select a company') }]}
                                >
                                    <Select
                                        size="large"
                                        placeholder={
                                            selectedCustomer ? t('Select a company') : t('Select a customer first')
                                        }
                                        disabled={!selectedCustomer}
                                        loading={loadingCompanies}
                                        options={companyOptions}
                                        onChange={(value, label) => {

                                            const companyLabel =
                                                typeof label === 'string'
                                                    ? label
                                                    : companyOptions.find((opt) => opt.value === value)?.label || '';

                                            //console.log('company label ', companyLabel);
                                            form.setFieldsValue({ companyName: companyLabel });

                                        }}
                                        showSearch
                                        filterOption={(input, option) =>
                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                        }
                                    />
                                </Form.Item>
                                <Form.Item name="companyName" noStyle>
                                    <Input type="hidden" />
                                </Form.Item>
                            </Col>
                            
                        )}

                    
                    {/*{uploadType === 'individual' && (*/}
                            <Col span={8}>
                                <Form.Item
                                label={t("Upload policy holder excel file")}
                                    name="file"
                                    rules={[{
                                        validator: () => file ? Promise.resolve() : Promise.reject(new Error(t("Please upload an policy holder excel file")))
                                    }]}
                                >
                                    <Upload {...uploadProps}>
                                    <Button icon={<UploadOutlined />}>{t("Select Excel File")}</Button>
                                    </Upload>
                                </Form.Item>
                            </Col>
                    </Row>

                    {/*)}*/}
                </Card>
                <Form.Item style={{ display: 'flex', marginTop: 16, justifyContent: 'flex-end' }}>
                    <Button type="primary" htmlType="submit" loading={uploadMutation.isPending}>
                        {t("Upload")}
                    </Button>
                </Form.Item>

                {uploadSummary && (
                    <div ref={resultsRef}>
                        <Card title={t("Upload Results")} style={{ marginTop: 16 }}>
                            <Row gutter={16} style={{ marginBottom: 16 }}>
                                <Col span={3}></Col>
                                <Col span={6}><Text><strong> {t("Total Records")}:</strong> {uploadSummary.totalRecords}</Text></Col>
                                <Col span={6}><Text type="success"><strong> {t("Records Saved")}:</strong> {uploadSummary.savedRecords}</Text></Col>
                                <Col span={6}><Text type="danger"><strong> {t("Records Failed")}:</strong> {uploadSummary.failedRecords}</Text></Col>
                                <Col span={3} style={{ textAlign: "right" }}>
                                    <Button
                                        style={{ backgroundColor: '#e6f7ff', borderColor: '#91d5ff', color: '#096dd9' }} 
                                        icon={<FileExcelOutlined />}
                                        onClick={() =>
                                            exportInvalidRecordsWithStyles(invalidRecords, [
                                                "name",
                                                "nationalIDNo",
                                                "mobileNo",
                                                "dob",
                                                "gender",
                                                "maritalStatus",
                                                "country",
                                                "city",
                                                "area",
                                                "streetNo",
                                                "address",
                                                "bloodGroupString",
                                                "category",
                                            ])
                                        }
                                    >
                                        {t("Export Error")}
                                    </Button>

                                </Col>
                            </Row>
                            {invalidRecords.length > 0 && (
                                <Table
                                    dataSource={invalidRecords}
                                    columns={tableColumns}
                                    rowKey="nationalIDNo"
                                    bordered
                                    scroll={{ x: 'max-content' }}
                                    expandable={{ expandedRowRender }}
                                />
                            )}
                        </Card>
                    </div>
                )}
            </Form>
        </>
    );
}